import { Schema } from "effect"
import { EntityRef, Owned, Tier } from "../common"
import {
  AgentId,
  AreaId,
  CommentId,
  HookId,
  IntakeId,
  KpiId,
  Millis,
  MonitorId,
  NotificationId,
  ProjectId,
  ProposalId,
  RunId,
  TaskId,
  UserId,
  WakeId,
  WorkspaceId,
} from "../ids"
import { MetricCondition } from "./plan"

// #region wakes
/** Why an agent wakes. Purely event-driven; time-based wakes only through explicit monitors. */
export const WakeReason = Schema.TaggedUnion({
  /** chat (owner: steer or new turn). `text`: the message's text (fix wave §4.7, for `.memory/turn.md`). */
  user_message: { messageId: Schema.String, from: UserId, text: Schema.optionalKey(Schema.String) },
  human_mention: { commentId: CommentId, from: UserId, untrusted: Schema.Boolean }, // a PERSON's @mention; agents' mentions never wake
  assignment: { taskId: TaskId },
  run_ended: { runId: RunId, taskId: Schema.NullOr(TaskId), outcome: Schema.String }, // a worker/validator on its task finished
  review_requested: { taskId: TaskId, round: Schema.Int }, // its "agent" review stage
  blocker: { taskId: TaskId, runId: Schema.NullOr(RunId) },
  impossible_claimed: { taskId: TaskId, runId: RunId },
  ask: { askId: CommentId }, // an ask addressed to it (from one level below)
  answer: { askId: CommentId }, // an answer to its own ask
  proposal_resolved: { proposalId: ProposalId, state: Schema.String }, // one of its own proposals
  area_request: { taskId: TaskId }, // a cross-area request for its area (it is the intake agent)
  intake: { intakeId: IntakeId }, // new intake for the project (the lead; coalesced with other new intake)
  kpi_threshold: { kpiId: KpiId }, // Phase 3
  monitor: { monitorId: MonitorId, kind: Schema.String },
})

/** One coalesced batch: ten finished tasks wake the agent once. It becomes the turn run's idempotency key. */
export const WakeBatch = Schema.Struct({
  wakeBatchId: WakeId,
  reasons: Schema.NonEmptyArray(WakeReason),
  coalescedFrom: Schema.Int,
})
// #endregion wakes

// #region monitor
/** What fires a monitor. Webhooks and KPI thresholds are Phase 2/3 triggers for routines (decision 0002). */
export const MonitorTrigger = Schema.TaggedUnion({
  cron: { expr: Schema.String, tz: Schema.String },
  at: { at: Millis },
  webhook: { hookId: HookId },
  kpi_threshold: { condition: MetricCondition },
})

/**
 * Explicit wakes that are not caused by a graph event: heartbeat, digest, weekly review, curation, KPI check, bet
 * evaluation, and agent routines (decision 0002). Stored in the Agent DO of the agent it wakes (the body owns its
 * schedules); `owner` is who it belongs to (an agent, or an Area whose agent it wakes). A monitor only wakes its
 * agent: the turn is visible and may create intake items or proposals, never hidden work. Routines cannot create
 * routines: a SetMonitor{kind: routine} from a turn whose wake batch came from a routine is rejected.
 */
export const Monitor = Schema.Struct({
  id: MonitorId,
  ...Owned,
  agentId: AgentId, // the agent it wakes
  owner: Schema.TaggedUnion({ agent: { agentId: AgentId }, area: { projectId: ProjectId, areaId: AreaId } }),
  projectId: Schema.NullOr(ProjectId),
  kind: Schema.Literals([
    "heartbeat",
    "digest",
    "weekly_review",
    "curation",
    "kpi_check",
    "bet_eval",
    "routine",
  ]),
  trigger: MonitorTrigger,
  subject: Schema.NullOr(EntityRef),
  instruction: Schema.String, // what the wake is about, shown in the agent's inbox
  /** A fire while its previous turn is still active: skip it, or coalesce into the next batch. Missed fires
   *  (the DO was asleep past the time) are always skipped, never replayed. */
  ifActive: Schema.Literals(["skip", "coalesce"]),
  quietHours: Schema.NullOr(Schema.Struct({ from: Schema.String, to: Schema.String, tz: Schema.String })),
  /** failure → blocked: a routine whose turn failed stops firing until a person resumes it. */
  state: Schema.TaggedUnion({ active: {}, blocked: { reason: Schema.String, runId: Schema.NullOr(RunId) } }),
  /** Lineage: the routine whose turn created this monitor (never another routine). */
  createdByRoutine: Schema.NullOr(MonitorId),
  enabled: Schema.Boolean,
  lastFiredAt: Schema.NullOr(Millis),
})
export type Monitor = typeof Monitor.Type
// #endregion monitor

// #region notification
/** Per recipient, in the recipient's User DO (fan-out on write). Needs-you = tier ∈ {blocking, soon}, not done. */
export const Notification = Schema.Struct({
  id: NotificationId,
  userId: UserId,
  workspaceId: WorkspaceId,
  projectId: Schema.NullOr(ProjectId),
  source: Schema.TaggedUnion({
    proposal: { proposalId: ProposalId },
    mention: { commentId: CommentId },
    task: { taskId: TaskId, event: Schema.String },
    kpi: { kpiId: KpiId },
    runner: { runnerId: Schema.String, state: Schema.String },
    digest: { digestRef: Schema.String },
  }),
  tier: Tier,
  title: Schema.String,
  reason: Schema.String, // "why now"
  groupKey: Schema.String, // collapses bursts: "Mara finished 6 tasks"
  count: Schema.Int,
  /** Outward approvals are never actionable from the lock screen: the push only opens the preview. */
  actionableFromNotification: Schema.Boolean,
  createdAt: Millis,
  seenAt: Schema.NullOr(Millis),
  doneAt: Schema.NullOr(Millis), // acted on, or the source resolved elsewhere
  snoozedUntil: Schema.NullOr(Millis),
  push: Schema.Literals(["none", "pending", "sent", "batched", "quiet_hours"]),
})

export const NotificationPrefs = Schema.Struct({
  userId: UserId,
  quietHours: Schema.NullOr(Schema.Struct({ from: Schema.String, to: Schema.String, tz: Schema.String })),
  soonBatchMinutes: Schema.Int,
  /** Per-source overrides; outward approvals can't be demoted below "soon". */
  overrides: Schema.Array(Schema.Struct({ source: Schema.String, tier: Tier })),
  digestCron: Schema.NullOr(Schema.String),
})
// #endregion notification
